import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Calendar, CheckCircle, XCircle, HelpCircle, ChevronDown, ChevronUp, Clock, MapPin } from 'lucide-react';
import { trainingAPI } from '../services/api';
import { format, parseISO } from 'date-fns';

const NextTrainingWidget = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await trainingAPI.getNextAttendanceSheet();
                setData(response.data);
            } catch (error) {
                console.error('Failed to fetch next training sheet:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (loading) return null;
    if (!data || !data.event) return null;

    const { event, attendees } = data;
    const attendingCount = attendees.filter(a => a.status === 'attending').length;
    const notAttendingCount = attendees.filter(a => a.status === 'not_attending').length;
    const pendingCount = attendees.filter(a => a.status === 'pending').length;
    const totalCount = attendees.length;

    const participationRate = totalCount > 0 ? Math.round((attendingCount / totalCount) * 100) : 0;

    return (
        <Card className="bg-[#1a1a1b] border-gray-800 mb-6">
            <CardHeader className="border-b border-gray-800 py-4 cursor-pointer hover:bg-white/5 transition-colors" onClick={() => setExpanded(!expanded)}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="bg-blue-600/20 p-2 rounded-lg">
                            <Calendar className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                            <h3 className="text-white font-medium flex items-center gap-2">
                                Bir Sonraki İdman
                                <Badge variant="outline" className="text-xs border-blue-500/50 text-blue-400 bg-blue-500/10">
                                    {format(parseISO(event.start_datetime), 'd MMM, HH:mm')}
                                </Badge>
                            </h3>
                            <div className="text-sm text-gray-400 flex items-center gap-2 mt-1">
                                <span className="text-green-400 font-medium">{attendingCount} Katılıyor</span>
                                <span>•</span>
                                <span>{pendingCount} Bekliyor</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="text-right hidden sm:block">
                            <div className="text-2xl font-bold text-white">%{participationRate}</div>
                            <div className="text-xs text-gray-500">Katılım Oranı</div>
                        </div>
                        {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                    </div>
                </div>
            </CardHeader>

            {expanded && (
                <CardContent className="p-0">
                    <div className="bg-[#0f0f10] p-4 border-b border-gray-800 flex items-center gap-6 text-sm">
                        <div className="flex items-center gap-2 text-gray-300">
                            <Clock className="w-4 h-4 text-gray-500" />
                            {format(parseISO(event.start_datetime), 'HH:mm')} - {event.end_datetime ? format(parseISO(event.end_datetime), 'HH:mm') : '?'}
                        </div>
                        {event.location && (
                            <div className="flex items-center gap-2 text-gray-300">
                                <MapPin className="w-4 h-4 text-gray-500" />
                                {event.location}
                            </div>
                        )}
                    </div>

                    <div className="divide-y divide-gray-800 max-h-[400px] overflow-y-auto">
                        {attendees.sort((a, b) => {
                            // Sort: Attending first, then Pending, then Not Attending
                            const order = { attending: 0, pending: 1, not_attending: 2 };
                            return order[a.status] - order[b.status];
                        }).map((user) => (
                            <div key={user.user_id} className="flex items-center justify-between p-3 hover:bg-white/5 transition-colors">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-xs font-bold text-gray-300">
                                        {user.avatar_url ? (
                                            <img src={user.avatar_url} alt={user.username} className="w-full h-full rounded-full object-cover" />
                                        ) : (
                                            user.full_name?.charAt(0) || user.username?.charAt(0)
                                        )}
                                    </div>
                                    <div>
                                        <div className="text-sm font-medium text-white">{user.full_name || user.username}</div>
                                        <div className="text-xs text-gray-500 capitalize">{user.role}</div>
                                    </div>
                                </div>

                                <div className="flex items-center">
                                    {user.status === 'attending' && (
                                        <Badge className="bg-green-600/20 text-green-400 border-none hover:bg-green-600/20">
                                            <CheckCircle className="w-3 h-3 mr-1" /> Katılıyor
                                        </Badge>
                                    )}
                                    {user.status === 'not_attending' && (
                                        <Badge className="bg-red-600/20 text-red-400 border-none hover:bg-red-600/20">
                                            <XCircle className="w-3 h-3 mr-1" /> Katılmıyor
                                        </Badge>
                                    )}
                                    {user.status === 'pending' && (
                                        <Badge variant="outline" className="border-gray-700 text-gray-400">
                                            <HelpCircle className="w-3 h-3 mr-1" /> Bekliyor
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </CardContent>
            )}
        </Card>
    );
};

export default NextTrainingWidget;
